import { useTranslation } from 'react-i18next';
import { useLastExecution, useRunProcess, useExecutions } from '../../api/executions.js';
import { isActiveStatus, processTypeLabel } from '../../utils/constants.js';
import { formatDateTime } from '../../utils/format.js';
import Spinner from '../Spinner.jsx';
import ErrorBanner from '../ErrorBanner.jsx';
import StatusBadge from '../StatusBadge.jsx';

export default function ExecutionsPanel({ companyId, processId, selectedExecutionId, onSelect }) {
    const { t, i18n } = useTranslation();
    const { data: lastExecution } = useLastExecution(companyId, processId);
    const { data: executions, isLoading, error } = useExecutions(companyId, processId);
    const runProcess = useRunProcess(companyId, processId);

    const process = lastExecution?.process;
    const active = isActiveStatus(lastExecution?.status);
    const canRun = process?.enabled && !active;

    const sorted = executions ? [...executions].sort((a, b) => new Date(b.startDate) - new Date(a.startDate)) : [];

    return (
        <div className="workspace-panel">
            <div className="panel-header">
                <div className="panel-header-titles">
                    <h2>{t('executions.title')}</h2>
                    {process && <div className="panel-subtitle">{processTypeLabel(process.type, t)}</div>}
                </div>
                {processId && (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={!canRun || runProcess.isPending}
                        title={
                            !process?.enabled
                                ? t('executions.enableFirst')
                                : active
                                  ? t('executions.alreadyRunning')
                                  : undefined
                        }
                        onClick={() => runProcess.mutate()}
                    >
                        {runProcess.isPending ? t('executions.starting') : t('executions.run')}
                    </button>
                )}
            </div>

            {!processId && <div className="panel-empty">{t('executions.selectProcessPrompt')}</div>}

            {processId && (
                <div className="panel-body">
                    <ErrorBanner error={runProcess.error} fallback={t('executions.startError')} />
                    {isLoading && <Spinner />}
                    <ErrorBanner error={error} fallback={t('executions.loadError')} />

                    {!isLoading && sorted.length === 0 && (
                        <div className="panel-empty">{t('executions.empty')}</div>
                    )}

                    {sorted.map((execution) => (
                        <button
                            key={execution.id}
                            type="button"
                            className={`panel-list-item ${execution.id === selectedExecutionId ? 'active' : ''}`}
                            onClick={() => onSelect(execution.id)}
                        >
                            <span className="item-title">
                                {formatDateTime(execution.startDate, i18n.language)}
                                <StatusBadge status={execution.status} />
                            </span>
                            {!isActiveStatus(execution.status) && execution.endDate && (
                                <span className="item-meta">
                                    {t('executions.ended', { date: formatDateTime(execution.endDate, i18n.language) })}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
